import { SAVE_KEY, defaultSave, migrateSave, type SaveData } from './saveData';

/** The slice of the Web Storage API we use — lets tests inject an in-memory store. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class MemoryStorage implements StorageLike {
  readonly map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

export type LoadStatus = 'fresh' | 'loaded' | 'migrated' | 'corrupt' | 'unavailable';

export interface LoadResult {
  data: SaveData;
  status: LoadStatus;
}

/** Resolve localStorage defensively (private mode / blocked storage throws on access). */
export function browserStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const probe = '__bh_probe';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads and writes the single save slot. Corrupt data is moved to `<key>.corrupt` (so a bug report can
 * still recover it) and replaced with defaults; writes go through a temp key so a crash mid-write cannot
 * destroy the previous good save.
 */
export class SaveStore {
  constructor(
    private readonly storage: StorageLike | null,
    private readonly key = SAVE_KEY,
    private readonly now: () => number = () => Date.now(),
  ) {}

  get available(): boolean {
    return this.storage !== null;
  }

  load(): LoadResult {
    if (!this.storage) return { data: defaultSave(), status: 'unavailable' };
    let text: string | null;
    try {
      text = this.storage.getItem(this.key);
    } catch {
      return { data: defaultSave(), status: 'unavailable' };
    }
    if (text === null) return { data: defaultSave(), status: 'fresh' };
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      this.quarantine(text);
      return { data: defaultSave(), status: 'corrupt' };
    }
    const res = migrateSave(parsed);
    if (!res.valid) {
      this.quarantine(text);
      return { data: res.data, status: 'corrupt' };
    }
    return { data: res.data, status: res.migrated ? 'migrated' : 'loaded' };
  }

  save(data: SaveData): boolean {
    if (!this.storage) return false;
    try {
      const out: SaveData = { ...data, savedAt: this.now() };
      const tmp = `${this.key}.tmp`;
      const json = JSON.stringify(out);
      this.storage.setItem(tmp, json);
      this.storage.setItem(this.key, json);
      this.storage.removeItem(tmp);
      return true;
    } catch {
      return false; // quota / blocked — progress simply isn't persisted
    }
  }

  clear(): void {
    try {
      this.storage?.removeItem(this.key);
    } catch {
      /* ignore */
    }
  }

  private quarantine(text: string): void {
    try {
      this.storage?.setItem(`${this.key}.corrupt`, text.slice(0, 20_000));
    } catch {
      /* ignore */
    }
  }
}
